"use client";

import { Box, Button, HStack, Text, VStack } from "@chakra-ui/react";
import GenericIcon from "@/shared/ui/actions/GenericIcon";
import { PROFILE_SHAPING_LABELS } from "@/features/profile-shaping/config/actions";
import type { GenericIconType } from "@/shared/ui/actions/GenericIcon";
import { SEGMENT_LABEL_PROPS } from "./studioChrome.constants";
import type { StudioPrototypeTone } from "./studioChrome.types";

type StudioModePanelProps = {
  tone: StudioPrototypeTone;
  isOrderMode: boolean;
  isNeedsAnchorMode: boolean;
  maintenanceModeHumanReadable: string | null;
  isPreviewUnavailable: boolean;
  previewButtonColorPalette: string;
  previewButtonIcon: GenericIconType;
  previewButtonHumanReadable: string;
  reviewDescription: string;
  shouldShowReviewDescription: boolean;
  onTogglePreviewClick: () => void;
  onOpenRerankDialog: () => void;
  onExitMaintenanceMode: () => void;
};

const REVIEW_DESCRIPTION_STYLE = {
  color: "var(--chakra-colors-moveon-text-muted)",
  fontSize: "var(--chakra-font-sizes-sm)",
  lineHeight: "var(--chakra-line-heights-tall)",
} as const;

export default function StudioModePanel({
  tone,
  isOrderMode,
  isNeedsAnchorMode,
  maintenanceModeHumanReadable,
  isPreviewUnavailable,
  previewButtonColorPalette,
  previewButtonIcon,
  previewButtonHumanReadable,
  reviewDescription,
  shouldShowReviewDescription,
  onTogglePreviewClick,
  onOpenRerankDialog,
  onExitMaintenanceMode,
}: StudioModePanelProps) {
  const activeModeLabel = isNeedsAnchorMode
    ? PROFILE_SHAPING_LABELS.needsAnchor
    : isOrderMode
      ? "Order mode"
      : "Search mode";
  const activeModeIcon: GenericIconType = isNeedsAnchorMode
    ? "situation"
    : isOrderMode
      ? "rerank"
      : "search";
  const activeModeHumanReadable =
    maintenanceModeHumanReadable ?? activeModeLabel;
  const isHighlightedMode = isOrderMode || isNeedsAnchorMode;

  return (
    <Box
      borderWidth="1"
      borderInlineStartWidth={isHighlightedMode ? "3" : "1"}
      borderColor="moveon.surface.border"
      borderInlineStartColor={
        isHighlightedMode ? tone.accent : "moveon.surface.border"
      }
      borderRadius="md"
      bg="moveon.surface.bg"
      px={{ base: "2.5", md: "4" }}
      py={{ base: "2.5", md: "4" }}
    >
      <VStack align="stretch" gap={{ base: "2", md: "2.5" }}>
        <HStack
          justify="space-between"
          align={{ base: "stretch", md: "center" }}
          gap="3"
          flexWrap="wrap"
        >
          <HStack gap="2" align="center" flexWrap="wrap">
            <GenericIcon
              icon={activeModeIcon}
              humanReadable={activeModeHumanReadable}
              size="xs"
              color={isHighlightedMode ? tone.accent : undefined}
            />
            <Text {...SEGMENT_LABEL_PROPS}>
              <Text as="span" display={{ base: "inline", md: "none" }}>
                {activeModeLabel}
              </Text>
              <Text as="span" display={{ base: "none", md: "inline" }}>
                {activeModeLabel}
              </Text>
            </Text>
          </HStack>

          <HStack gap="2" flexWrap="wrap" justify="flex-end">
            {isNeedsAnchorMode ? (
              <Button
                variant="solid"
                colorPalette={tone.colorPalette}
                minH={{ base: "9", md: "10" }}
                px={{ base: "3", md: "4" }}
                onClick={onExitMaintenanceMode}
                title="Return to normal Studio"
                aria-label="Return to normal Studio"
              >
                <GenericIcon
                  icon="close"
                  humanReadable="Return to normal Studio"
                />
                <Text as="span">Done</Text>
              </Button>
            ) : isOrderMode ? (
              <Button
                variant="outline"
                minH={{ base: "9", md: "10" }}
                px={{ base: "3", md: "4" }}
                onClick={onOpenRerankDialog}
              >
                <GenericIcon
                  icon="profile_focus"
                  humanReadable={PROFILE_SHAPING_LABELS.tailorToFocus}
                />
                <Text as="span" display={{ base: "none", sm: "inline" }}>
                  {PROFILE_SHAPING_LABELS.tailorToFocus}
                </Text>
              </Button>
            ) : null}

            {isNeedsAnchorMode ? null : (
              <Button
                variant={isOrderMode ? "solid" : "ghost"}
                colorPalette={
                  isOrderMode ? tone.colorPalette : previewButtonColorPalette
                }
                onClick={onTogglePreviewClick}
                title={previewButtonHumanReadable}
                aria-label={previewButtonHumanReadable}
                minH={{ base: "9", md: "10" }}
                px={{ base: "3", md: "4" }}
                disabled={isPreviewUnavailable}
              >
                <GenericIcon
                  icon={previewButtonIcon}
                  humanReadable={previewButtonHumanReadable}
                />
                {isOrderMode ? (
                  <>
                    <Text as="span" display={{ base: "inline", sm: "none" }}>
                      Search
                    </Text>
                    <Text as="span" display={{ base: "none", sm: "inline" }}>
                      Return to search
                    </Text>
                  </>
                ) : !isPreviewUnavailable ? (
                  <>
                    <Text as="span" display={{ base: "inline", sm: "none" }}>
                      Order
                    </Text>
                    <Text as="span" display={{ base: "none", sm: "inline" }}>
                      Order output
                    </Text>
                  </>
                ) : null}
              </Button>
            )}
          </HStack>
        </HStack>

        {shouldShowReviewDescription ? (
          <p style={REVIEW_DESCRIPTION_STYLE}>
            {isNeedsAnchorMode ? (
              reviewDescription
            ) : isOrderMode ? (
              <>
                <Text as="span" display={{ base: "inline", md: "none" }}>
                  Fine-tune order before export.
                </Text>
                <Text as="span" display={{ base: "none", md: "inline" }}>
                  {reviewDescription}
                </Text>
              </>
            ) : (
              reviewDescription
            )}
          </p>
        ) : null}
      </VStack>
    </Box>
  );
}
